import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { api } from "aldoo-ra/API"
import { showNotification } from "aldoo-ra/Notification"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Input from "aldoo-ra/Input"
import BackgroundOverlay from "./BackgroundOverlay.jsx"

const NewsletterManagePage = ({ pageContext }) => {
  const newsletterAPI = api('newsletter/manage')

  const [subscriberId, setSubscriberId] = useState(null)
  const [subscriberData, setSubscriberData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [isInvalidId, setIsInvalidId] = useState(false)

  // Error display component
  const ErrorDisplay = () => (
    <div className="relative min-h-screen bg-canvas transition-colors flex items-center justify-center">
      <BackgroundOverlay />
      <div className="relative max-w-md mx-auto text-center" style={{ zIndex: 20 }}>
        <Typography variant="h3" className="text-error mb-4">
          Subscriber not found
        </Typography>
        <Typography variant="body_16" className="text-text_secondary mb-8">
          We don't have this subscriber in our system. Please check your link.
        </Typography>
        <div className="flex justify-center">
          <Button
            variant="admin_primary"
            onClick={() => window.location.href = '/'}
          >
            Go Back Home
          </Button>
        </div>
      </div>
    </div>
  )

  // MongoDB ObjectId validation
  const isValidMongoId = (id) => {
    if (!id || typeof id !== 'string') return false

    // MongoDB ObjectId is 24 characters long and contains only hexadecimal characters
    const objectIdRegex = /^[0-9a-fA-F]{24}$/
    return objectIdRegex.test(id)
  }

  // Common unsubscribe reasons
  const unsubscribeReasons = [
    { value: 'Too many emails', label: 'Too many emails' },
    { value: 'Not relevant content', label: 'Content is not relevant to me' },
    { value: 'Never signed up', label: 'I never signed up for this' },
    { value: 'Spam concerns', label: 'Emails feel like spam' },
    { value: 'Frequency too high', label: 'Emails are sent too frequently' },
    { value: 'Poor content quality', label: 'Content quality is poor' },
    { value: 'Changed interests', label: 'My interests have changed' },
    { value: 'Technical issues', label: 'Technical issues with emails' },
    { value: 'Privacy concerns', label: 'Privacy and data concerns' },
    { value: 'Other', label: 'Other (please specify)' }
  ]

  // Extract subscriber ID from URL params on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const manageId = urlParams.get('manage')

      if (manageId) {
        // Hide navbar and footer using DOM manipulation
        const navbar = document.querySelector('[role="navbar"]')
        const footer = document.querySelector('[role="footer"]')
        
        if (navbar) navbar.style.display = 'none'
        if (footer) footer.style.display = 'none'
      }

      if (!manageId) {
        setError('No subscriber ID provided in URL')
        setIsLoading(false)
        return
      }

      // Validate MongoDB ObjectId format
      if (!isValidMongoId(manageId)) {
        setIsInvalidId(true)
        setIsLoading(false)
        return
      }

      setSubscriberId(manageId)
      fetchSubscriberData(manageId)
    }

    // Cleanup on component unmount
    return () => {
      if (typeof window !== 'undefined') {
        const navbar = document.querySelector('[role="navbar"]')
        const footer = document.querySelector('[role="footer"]')
        
        if (navbar) navbar.style.display = ''
        if (footer) footer.style.display = ''
      }
    }
  }, [])

  // Fetch subscriber data
  const fetchSubscriberData = async (id) => {
    try {
      setIsLoading(true)

      const response = await newsletterAPI({
        info: {
          id: id
        }
      })

      if (response.success && response.subscriber) {
        setSubscriberData(response.subscriber)
      } else {
        setError('Subscriber not found or invalid link')
      }
    } catch (error) {
      console.error('Error fetching subscriber:', error)
      setError('Failed to load subscriber information')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle unsubscribe
  const handleUnsubscribe = async (e) => {
    e.preventDefault()

    if (!selectedReason && !customReason) {
      setError('Please select or enter a reason for unsubscribing')
      return
    }

    const reason = selectedReason === 'Other' ? customReason : selectedReason

    if (!reason.trim()) {
      setError('Please provide a reason for unsubscribing')
      return
    }

    if (reason.length > 500) {
      setError('Reason must be 500 characters or less')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const response = await newsletterAPI({
        unsubscribe: {
          id: subscriberId,
          reason: reason.trim()
        }
      })

      if (response.success) {
        showNotification({
          message: 'Successfully unsubscribed from newsletter',
          className: "bg-success text-text_inv",
        })

        // Refresh subscriber data to show updated status
        await fetchSubscriberData(subscriberId)
      } else {
        setError(response.message || 'Failed to unsubscribe')
      }
    } catch (error) {
      console.error('Error unsubscribing:', error)
      setError('Failed to process unsubscribe request')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle resubscribe
  const handleResubscribe = async () => {
    setIsSubmitting(true)
    setError('')

    try {
      const response = await newsletterAPI({
        subscribe: {
          id: subscriberId
        }
      })

      if (response.success) {
        showNotification({
          message: 'Successfully resubscribed to newsletter',
          className: "bg-success text-text_inv",
        })

        // Refresh subscriber data to show updated status
        await fetchSubscriberData(subscriberId)
      } else {
        setError(response.message || 'Failed to resubscribe')
      }
    } catch (error) {
      console.error('Error resubscribing:', error)
      setError('Failed to process resubscribe request')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-canvas transition-colors flex items-center justify-center">
        <BackgroundOverlay />
        <div className="relative text-center" style={{ zIndex: 20 }}>
          <Typography variant="h3" className="text-text mb-4">
            Loading...
          </Typography>
          <Typography variant="body_16" className="text-text_secondary">
            Please wait while we load your subscription details
          </Typography>
        </div>
      </div>
    )
  }

  if (isInvalidId) {
    return <ErrorDisplay />
  }

  if (error && !subscriberData) {
    return <ErrorDisplay />
  }

  return (
    <div className="relative min-h-screen bg-canvas transition-colors py-16">
      <BackgroundOverlay />
      <div className="relative max-w-2xl mx-auto px-4" style={{ zIndex: 20 }}>
        {/* Header */}
        <div className="text-center mb-12">
          <Typography variant="h3" className="text-text mb-4">
            Newsletter Preferences
          </Typography>
          <Typography variant="body_20" className="text-text_secondary">
            Manage your newsletter subscription settings
          </Typography>
        </div>

        {/* Subscriber Info Card */}
        {subscriberData && (
          <div className="bg-paper/40 glass_effect rounded-[24px] p-8 mb-8 transition-colors">
            <Typography variant="h4" className="text-text mb-4">
              Subscription Details
            </Typography>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <Typography
                  variant="body_14"
                  className="text-text_secondary mb-1"
                >
                  Email Address
                </Typography>
                <Typography variant="body_16" className="text-text font-medium">
                  {subscriberData.email}
                </Typography>
              </div>

              <div>
                <Typography
                  variant="body_14"
                  className="text-text_secondary mb-1"
                >
                  Status
                </Typography>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    subscriberData.active
                      ? "bg-success/20 text-success border border-success"
                      : "bg-error/20 text-error border border-error"
                  }`}
                >
                  {subscriberData.active ? "Active" : "Unsubscribed"}
                </span>
              </div>

              {subscriberData.subscribedAt && (
                <div>
                  <Typography
                    variant="body_14"
                    className="text-text_secondary mb-1"
                  >
                    Subscribed
                  </Typography>
                  <Typography variant="body_16" className="text-text">
                    {new Date(subscriberData.subscribedAt).toLocaleDateString()}
                  </Typography>
                </div>
              )}

              {subscriberData.platforms &&
                subscriberData.platforms.length > 0 && (
                  <div>
                    <Typography
                      variant="body_14"
                      className="text-text_secondary mb-1"
                    >
                      Platform
                    </Typography>
                    <Typography variant="body_16" className="text-text">
                      {subscriberData.platforms.join(", ")}
                    </Typography>
                  </div>
                )}
            </div>

            {/* Show unsubscribe reason if exists */}
            {!subscriberData.active && subscriberData.unsubscribeReason && (
              <div className="border-t border-admin_bordered pt-4">
                <Typography
                  variant="body_14"
                  className="text-text_secondary mb-1"
                >
                  Unsubscribe Reason
                </Typography>
                <Typography variant="body_16" className="text-text">
                  {subscriberData.unsubscribeReason}
                </Typography>
              </div>
            )}
          </div>
        )}

        {/* Action Cards */}
        <div className="space-y-6">
          {subscriberData?.active ? (
            /* Unsubscribe Form */
            <div className="bg-paper/40 glass_effect rounded-[24px] p-8 transition-colors">
              <Typography variant="h4" className="text-text mb-6">
                Unsubscribe from Newsletter
              </Typography>

              <Typography
                variant="body_16"
                className="text-text_secondary mb-6"
              >
                We're sorry to see you go. Please let us know why you're
                unsubscribing so we can improve our newsletter.
              </Typography>

              <form onSubmit={handleUnsubscribe} className="space-y-6">
                {/* Reason Dropdown */}
                <div>
                  <Typography
                    variant="body_16"
                    className="text-text mb-3 font-medium"
                  >
                    Please tell us why you're unsubscribing *
                  </Typography>
                  <DropdownSelect
                    value={
                      selectedReason
                        ? [
                            {
                              value: selectedReason,
                              label:
                                unsubscribeReasons.find(
                                  (r) => r.value === selectedReason
                                )?.label || selectedReason,
                            },
                          ]
                        : []
                    }
                    onChange={(selected) =>
                      setSelectedReason(selected[0]?.value || "")
                    }
                    options={unsubscribeReasons}
                    placeholder="Choose your reason..."
                    className="w-full"
                    enableResetButton={false}
                    showSelectionTick={true}
                    showCheckbox={false}
                    variant="default"
                    multiSelection={false}
                  />
                </div>

                {/* Custom Reason Input (shown when "Other" is selected) */}
                {selectedReason === "Other" && (
                  <div>
                    <Typography
                      variant="body_16"
                      className="text-text mb-2 font-medium"
                    >
                      Please specify your reason
                    </Typography>
                    <Input
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Please tell us your specific reason..."
                      maxLength={500}
                      className="w-full"
                      multiline
                      rows={3}
                    />
                    <Typography
                      variant="body_12"
                      className="text-text_secondary mt-1"
                    >
                      {customReason.length}/500 characters
                    </Typography>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="p-3 bg-error/10 border border-error rounded-[8px] transition-colors">
                    <Typography variant="body_16" className="text-error my-0">
                      {error}
                    </Typography>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4">
                  <Button
                    type="submit"
                    variant="admin_primary"
                    disabled={
                      isSubmitting || (!selectedReason && !customReason)
                    }
                    className="flex-1"
                  >
                    {isSubmitting ? "Unsubscribing..." : "Unsubscribe"}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            /* Resubscribe Option */
            <div className="bg-paper/40 glass_effect rounded-[24px] p-8 transition-colors">
              <Typography variant="h4" className="text-text mb-4">
                Resubscribe to Newsletter
              </Typography>

              <Typography
                variant="body_16"
                className="text-text_secondary mb-6"
              >
                Want to start receiving our newsletter again? Click the button
                below to resubscribe.
              </Typography>

              {/* Error Display */}
              {error && (
                <div className="p-3 bg-error/10 border border-error rounded-[8px] transition-colors mb-6">
                  <Typography variant="body_16" className="text-error my-0">
                    {error}
                  </Typography>
                </div>
              )}

              <div className="flex justify-center">
                <Button
                  onClick={handleResubscribe}
                  variant="admin_primary"
                  disabled={isSubmitting}
                  className="w-full md:w-auto"
                >
                  {isSubmitting
                    ? "Resubscribing..."
                    : "Resubscribe to Newsletter"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col items-center mt-12">
          <Button
            variant="secondary"
            onClick={() => (window.location.href = "/")}
            className="mb-6"
          >
            Back Home
          </Button>
        </div>
      </div>
    </div>
  )
}

export default NewsletterManagePage